import { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { flushOfflineQueue, requestOfflineQueueSnapshot } from '@/lib/offline/registerServiceWorker';
import { formatOfflineReceiptCode } from '@/lib/offline/receipts';
import { useOfflineQueue } from '@/hooks/useOfflineQueue';
import { useOfflinePaymentTokens } from '@/hooks/useOfflinePaymentTokens';
import type { OfflinePaymentTokenStatus } from '@/lib/api/offlinePayments';
import { useOfflineRuntime } from '@/hooks/useOfflineRuntime';

const isDevRuntime = typeof import.meta !== 'undefined' && Boolean(import.meta.env?.DEV);

function formatTimestamp(value?: number): string {
  if (!value) {
    return '-';
  }
  const delta = Date.now() - value;
  if (delta < 0) {
    return 'just now';
  }
  const seconds = Math.floor(delta / 1000);
  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function resolveKindLabel(kind: string): string {
  switch (kind) {
    case 'pos-order':
      return 'POS';
    case 'public-order':
      return 'QR';
    case 'inventory':
      return 'Inventory';
    case 'kitchen':
      return 'Kitchen';
    case 'analytics':
      return 'Analytics';
    case 'ops':
      return 'Ops';
    default:
      return kind;
  }
}

function resolveReplayStatusBadge(
  status?: 'queued' | 'processing' | 'synced' | 'failed',
): { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' } | null {
  switch (status) {
    case 'queued':
      return { label: 'Queued for replay', variant: 'secondary' };
    case 'processing':
      return { label: 'Replaying…', variant: 'secondary' };
    case 'synced':
      return { label: 'Synced', variant: 'outline' };
    case 'failed':
      return { label: 'Failed', variant: 'destructive' };
    default:
      return null;
  }
}

export function OfflineQueueInspector(): JSX.Element | null {
  const { enabled: offlineRuntimeEnabled } = useOfflineRuntime();
  const [enabled] = useState(() => {
    if (typeof window === 'undefined') {
      return isDevRuntime;
    }
    try {
      const search = new URLSearchParams(window.location.search);
      if (search.has('offlineInspector')) {
        localStorage.setItem('offlineInspector', 'on');
        return true;
      }
      return isDevRuntime || localStorage.getItem('offlineInspector') === 'on';
    } catch {
      return isDevRuntime;
    }
  });

  const queue = useOfflineQueue();
  const offlinePaymentsQuery = useOfflinePaymentTokens({
    enabled,
    refetchInterval: 15_000,
    refetchOnWindowFocus: false,
  });
  const offlinePaymentTokens = useMemo(
    () => offlinePaymentsQuery.data ?? [],
    [offlinePaymentsQuery.data],
  );
  const offlineTokenMap = useMemo(() => {
    const map = new Map<string, OfflinePaymentTokenStatus>();
    for (const token of offlinePaymentTokens) {
      map.set(token.id, token);
      map.set(token.queueId, token);
    }
    return map;
  }, [offlinePaymentTokens]);
  const [open, setOpen] = useState(false);
  const [busyAction, setBusyAction] = useState<'flush' | 'snapshot' | null>(null);

  useEffect(() => {
    if (queue.retrying.length > 0) {
      setOpen(true);
    }
  }, [queue.retrying.length]);

  const stats = useMemo(() => [
    { label: 'Queued', value: queue.queued.length, variant: queue.queued.length ? 'default' : 'secondary' },
    { label: 'Retrying', value: queue.retrying.length, variant: queue.retrying.length ? 'destructive' : 'secondary' },
    { label: 'Synced (recent)', value: queue.synced.length, variant: 'outline' },
  ], [queue.queued.length, queue.retrying.length, queue.synced.length]);

  const hasEntries = queue.all.length > 0;

  const handleFlush = async () => {
    setBusyAction('flush');
    try {
      await flushOfflineQueue();
    } finally {
      setBusyAction((current) => (current === 'flush' ? null : current));
    }
  };

  const handleSnapshot = async () => {
    setBusyAction('snapshot');
    try {
      await requestOfflineQueueSnapshot();
    } finally {
      setBusyAction((current) => (current === 'snapshot' ? null : current));
    }
  };

  if (!enabled || !offlineRuntimeEnabled) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 z-[60] flex flex-col items-start gap-2 text-xs">
      <Button size="sm" variant={open ? 'default' : 'secondary'} onClick={() => setOpen((value) => !value)}>
        Offline Queue
        <Badge variant={queue.retrying.length ? 'destructive' : queue.queued.length ? 'default' : 'secondary'} className="ml-2">
          {queue.retrying.length || queue.queued.length}
        </Badge>
      </Button>

      {open && (
        <Card className="w-80 shadow-lg border-border/60">
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Offline Queue Inspector</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              {stats.map((stat) => (
                <Badge key={stat.label} variant={stat.variant as 'default' | 'secondary' | 'destructive' | 'outline'}>
                  {stat.label}: {stat.value}
                </Badge>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={handleSnapshot} disabled={busyAction === 'snapshot'}>
                {busyAction === 'snapshot' ? 'Refreshing…' : 'Refresh'}
              </Button>
              <Button size="sm" onClick={handleFlush} disabled={busyAction === 'flush'}>
                {busyAction === 'flush' ? 'Flushing…' : 'Flush queue'}
              </Button>
            </div>

            {!hasEntries ? (
              <Alert variant="secondary">
                <AlertDescription>No queued requests detected.</AlertDescription>
              </Alert>
            ) : (
              <ScrollArea className="max-h-64 pr-3">
                <div className="space-y-3">
                  {queue.all.map((entry) => {
                    const linkedToken = entry.meta.offlinePaymentTokenId
                      ? offlineTokenMap.get(entry.meta.offlinePaymentTokenId)
                      : undefined;
                    const replayBadge = resolveReplayStatusBadge(linkedToken?.replayStatus);
                    return (
                    <div key={entry.id} className="rounded-md border border-border/70 bg-muted/40 p-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium uppercase text-[10px] tracking-wide text-muted-foreground">
                          {resolveKindLabel(entry.meta.kind)} · {entry.status}
                        </span>
                        <span>{formatTimestamp(entry.createdAt)}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                        {entry.meta.tableId && <Badge variant="outline">Table {entry.meta.tableId}</Badge>}
                        {typeof entry.meta.itemCount === 'number' && entry.meta.itemCount > 0 && (
                          <Badge variant="outline">Items: {entry.meta.itemCount}</Badge>
                        )}
                        {typeof entry.meta.quantity === 'number' && Number.isFinite(entry.meta.quantity) && (
                          <Badge variant="outline">Qty: {entry.meta.quantity}</Badge>
                        )}
                        {typeof entry.meta.guestCount === 'number' && entry.meta.guestCount > 0 && (
                          <Badge variant="outline">Guests: {entry.meta.guestCount}</Badge>
                        )}
                        {entry.meta.resource && <Badge variant="outline">{entry.meta.resource}</Badge>}
                        {entry.meta.action && <Badge variant="outline">Action: {entry.meta.action}</Badge>}
                        {entry.meta.resourceId && entry.meta.resourceId !== entry.meta.clientRequestId && (
                          <Badge variant="outline">ID: {entry.meta.resourceId.slice(-8)}</Badge>
                        )}
                        {entry.meta.clientRequestId && (
                          <Badge variant="outline">ID: {entry.meta.clientRequestId.slice(-8)}</Badge>
                        )}
                        {entry.meta.authRequired && <Badge variant="destructive">Auth</Badge>}
                        {entry.attempts > 0 && <Badge variant="outline">Attempts: {entry.attempts}</Badge>}
                        {entry.nextAttemptAt && <Badge variant="outline">Next: {formatTimestamp(entry.nextAttemptAt)}</Badge>}
                        {linkedToken && (
                          <Badge variant="outline">
                            Receipt {formatOfflineReceiptCode(linkedToken.queueId ?? entry.meta.offlinePaymentTokenId ?? linkedToken.id)}
                          </Badge>
                        )}
                        {replayBadge && (
                          <Badge variant={replayBadge.variant}>
                            {replayBadge.label}
                          </Badge>
                        )}
                      </div>
                      {entry.lastError && (
                        <p className="mt-1 text-[11px] text-destructive">{entry.lastError}</p>
                      )}
                      {linkedToken?.lastError && linkedToken.replayStatus === 'failed' && (
                        <p className="mt-1 text-[11px] text-destructive">
                          Replay: {linkedToken.lastError}
                        </p>
                      )}
                    </div>
                  );
                  })}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default OfflineQueueInspector;
